import { useCallback } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  RegistryOrderConversation,
  RegistryOrderDetail,
  RegistryOrderReplyRequest,
} from '@/types/registry-documents';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';
import { REGISTRY_ORDERS_POLL_MS } from './useRegistryOrders';

/**
 * A report's Armadillo conversation (every order it sent and the mail on
 * them), polled with ETag revalidation at the orders card's cadence, and
 * the way to answer the research desk from it.
 *
 * `reportId` null switches the read off: the Partners tab reads it only
 * while Armadillo is listed there.
 *
 * `reply` sends through the registry reply path (the shared mailbox), then
 * re-reads, on failure too: a send that failed may still have recorded
 * nothing, but the conversation on screen is the one to trust after it.
 * The error is re-thrown for the reply dialog to show.
 */
export function useRegistryConversation(reportId: string | null) {
  const poll = usePoll<RegistryOrderConversation>({
    interval: REGISTRY_ORDERS_POLL_MS,
    enabled: reportId !== null,
    refetchDeps: [reportId],
    queryFn: async ({ signal, etag, manual }) => {
      if (!reportId) {
        return UNCHANGED;
      }
      const result = await vnextApiClient.getRegistryConversation(reportId, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch } = poll;

  const reply = useCallback(
    async (orderId: string, request: RegistryOrderReplyRequest): Promise<RegistryOrderDetail> => {
      try {
        return await vnextApiClient.replyToRegistryOrder(orderId, request);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  // The poll keeps its last payload across a change of report.
  const conversation =
    poll.data && reportId !== null && poll.data.reportId.toLowerCase() === reportId.toLowerCase()
      ? poll.data
      : null;

  return {
    conversation,
    loading: reportId !== null && conversation === null && poll.error === undefined,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    reply,
  };
}
